import { useCallback, useEffect, useRef, useState } from "react";
import type { DebugEvent, Status } from "@delegate/shared";
import { RealtimeSessionClient, type CharterView, type TranscriptEntry } from "./realtime.ts";
import { Briefing, type Resource } from "./Briefing.tsx";
import { api, ApiError, setAvatarId, type Me } from "./api.ts";
import { AvatarBar, Login, TenantAdmin } from "./Account.tsx";

type Costs = {
  session: { totalUsd: number; audioUsd: number; textUsd: number; calibratedUsd: number } | null;
  today: { totalUsd: number; audioUsd: number; calibratedUsd: number; byKind: { kind: string; costUsd: number }[] };
  allTime: { totalUsd: number; calibratedUsd: number };
  calibration: { factor: number; actualUsd: number; estimatedUsd: number; checkedAt: string; days: number } | null;
  reconciliationAvailable: boolean;
};

const usd = (v: number) => (v >= 1 ? `$${v.toFixed(2)}` : v >= 0.01 ? `$${v.toFixed(3)}` : `$${v.toFixed(4)}`);

type ServerState = {
  counts: Record<string, number>;
  facts: { key: string; value: unknown; tags: string[] }[];
  tasks: { title: string; status: string; summary: string; facts: Record<string, unknown> }[];
  toolEvents: { toolName: string; success: boolean; latencyMs: number; args: any; result: any }[];
};

const STATUS_COLORS: Record<Status, string> = {
  DISCONNECTED: "#64748b",
  CONNECTING: "#d97706",
  LISTENING: "#16a34a",
  SPEAKING: "#2563eb",
  TOOL_RUNNING: "#9333ea",
  ERROR: "#dc2626",
};

const EVENT_COLORS: Partial<Record<string, string>> = {
  USER_TURN: "#16a34a",
  ASSISTANT_TURN: "#2563eb",
  TOOL_CALL: "#9333ea",
  TOOL_ARGS: "#9333ea",
  MEMORY_HIT: "#059669",
  MEMORY_MISS: "#d97706",
  TOOL_RESULT: "#9333ea",
  TOOL_ERROR: "#dc2626",
  ERROR: "#dc2626",
  AUDIO_PLAYING: "#2563eb",
};

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [booting, setBooting] = useState(true);
  const [activeAvatar, setActiveAvatar] = useState<string | null>(null);
  const [showTenants, setShowTenants] = useState(false);
  const [status, setStatus] = useState<Status>("DISCONNECTED");
  const [events, setEvents] = useState<DebugEvent[]>([]);
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [state, setState] = useState<ServerState | null>(null);
  const [textInput, setTextInput] = useState("");
  const [costs, setCosts] = useState<Costs | null>(null);
  const [sessionUsd, setSessionUsd] = useState(0);
  const [charter, setCharter] = useState<CharterView | null>(null);
  const [briefingAck, setBriefingAck] = useState<string | null | undefined>(undefined);
  const [resources, setResources] = useState<Resource[]>([]);
  const [reconciling, setReconciling] = useState(false);
  const [rated, setRated] = useState<Record<number, number>>({});
  const [noteFor, setNoteFor] = useState<number | null>(null);
  const [noteText, setNoteText] = useState("");
  const clientRef = useRef<RealtimeSessionClient | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const refreshState = useCallback(async () => {
    const sid = clientRef.current?.localSessionId ?? "";
    const [stateRes, costRes] = await Promise.all([
      fetch(`/api/state?localSessionId=${encodeURIComponent(sid)}`),
      fetch(`/api/costs?localSessionId=${encodeURIComponent(sid)}`),
    ]);
    setState(await stateRes.json());
    if (activeAvatar) {
      // Provisioning is asynchronous; the poll below keeps this current while
      // it is still running.
      api<{ resources: Resource[] }>(`/api/avatars/${activeAvatar}/resources`)
        .then((d) => setResources(d.resources))
        .catch(() => setResources([]));
    }
    const c: Costs = await costRes.json();
    setCosts(c);
    if (c.session) setSessionUsd(c.session.totalUsd);
    // activeAvatar is a dependency: with an empty list this closed over its
    // value from the first render, which is null, so the resources fetch above
    // never ran and the panel was permanently empty.
  }, [activeAvatar]);

  const [billedToday, setBilledToday] = useState<number | null>(null);

  const reconcile = async () => {
    setReconciling(true);
    try {
      const res = await fetch("/api/reconcile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ days: 7 }),
      });
      const body = await res.json();
      // Today's billed figure is useful even when there is nothing to calibrate.
      if (typeof body?.today?.actualUsd === "number") setBilledToday(body.today.actualUsd);
      if (!body.ok) setError(`Reconcile: ${body.reason}`);
      await refreshState();
    } finally {
      setReconciling(false);
    }
  };

  /** Who am I, and which avatar am I talking to? Everything else depends on this. */
  const loadMe = useCallback(async () => {
    try {
      const next = await api<Me>("/api/auth/me");
      setMe(next);
      const chosen = activeAvatar && next.avatars.some((a) => a.id === activeAvatar)
        ? activeAvatar
        : (next.avatars[0]?.id ?? null);
      setActiveAvatar(chosen);
      setAvatarId(chosen);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setMe(null);
      else setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBooting(false);
    }
  }, [activeAvatar]);

  useEffect(() => {
    void loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Keep the resource panel current while something is still being built.
   *
   * Giving an org its own project workspace takes minutes. Fetching once when
   * the avatar is selected left the panel showing the first step for the whole
   * build, which is indistinguishable from it having hung - and the avatar
   * cannot do its job until it finishes, so this is the one thing the user
   * needs to see.
   */
  useEffect(() => {
    const building = resources.some((r) => r.status === "pending" || r.status === "provisioning");
    if (!building || !activeAvatar) return;
    const timer = setInterval(() => {
      api<{ resources: Resource[] }>(`/api/avatars/${activeAvatar}/resources`)
        .then((d) => setResources(d.resources))
        .catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
  }, [resources, activeAvatar]);

  useEffect(() => {
    if (me && activeAvatar) void refreshState();
  }, [me, activeAvatar, refreshState]);

  useEffect(() => {
    timelineRef.current?.scrollTo({ top: timelineRef.current.scrollHeight });
  }, [events]);

  const start = async () => {
    setError(null);
    setCharter(null);
    setBriefingAck(undefined);
    const client = new RealtimeSessionClient({
      onStatus: setStatus,
      onCost: setSessionUsd,
      onCharter: setCharter,
      onBriefingAck: setBriefingAck,
      onDebug: (e) => setEvents((prev) => [...prev, e]),
      onTranscript: (t) => setTranscript((prev) => [...prev, t]),
      onError: (m) => setError(m),
    });
    clientRef.current = client;
    try {
      await client.start();
      void refreshState();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
      setStatus("ERROR");
      client.stop();
    }
  };

  const stop = () => {
    clientRef.current?.stop();
    void refreshState();
  };

  const reset = async () => {
    await fetch("/api/reset", { method: "POST" });
    setEvents((p) => [
      ...p,
      { timestamp: new Date().toISOString(), localSessionId: "-", eventType: "RAW", payloadSummary: "all Delegate data reset" },
    ]);
    void refreshState();
  };

  /**
   * Score a single reply. The two turns either side go along with it, because a
   * rating without the exchange that produced it is not actionable later.
   */
  const rate = async (index: number, score: number, note = "") => {
    setRated((prev) => ({ ...prev, [index]: score }));
    const entry = transcript[index];
    await fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        localSessionId: clientRef.current?.localSessionId ?? "unknown",
        targetKind: "turn",
        targetRef: entry?.timestamp,
        score,
        note,
        context: {
          assistant: entry?.text,
          precedingUser: transcript[index - 1]?.role === "user" ? transcript[index - 1].text : undefined,
          followingUser: transcript[index + 1]?.role === "user" ? transcript[index + 1].text : undefined,
        },
      }),
    }).catch(() => {});
  };

  const submitNote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (noteFor === null) return;
    await rate(noteFor, rated[noteFor] ?? -1, noteText.trim());
    setNoteFor(null);
    setNoteText("");
  };

  const sendText = (e: React.FormEvent) => {
    e.preventDefault();
    if (!textInput.trim()) return;
    clientRef.current?.sendText(textInput.trim());
    setTextInput("");
  };

  const connected = status !== "DISCONNECTED" && status !== "ERROR";

  const switchAvatar = (id: string) => {
    // Switching mid-call would leave the old session running against the wrong
    // scope, so stop first.
    if (connected) clientRef.current?.stop();
    setActiveAvatar(id);
    setAvatarId(id);
    setTranscript([]);
    setEvents([]);
    setSessionUsd(0);
    setCharter(null);
    setBriefingAck(undefined);
  };

  if (booting) return <div className="login"><p className="muted">Loading…</p></div>;
  if (!me) return <Login onSignedIn={() => void loadMe()} />;

  // One source of truth for who is speaking: the heading and every assistant
  // bubble previously disagreed, because the bubble said "Ava" whoever it was.
  const avatarName = me?.avatars.find((a) => a.id === activeAvatar)?.name ?? "Delegate";

  return (
    <div className="app">
      <AvatarBar
        me={me}
        activeId={activeAvatar}
        onSelect={switchAvatar}
        onChanged={() => void loadMe()}
        disabled={connected}
      />
      {showTenants && <TenantAdmin onClose={() => setShowTenants(false)} />}
      <header>
        <h1>{avatarName}</h1>
        <div className="status" style={{ background: STATUS_COLORS[status] }}>
          {status}
        </div>
        <div className="spend" title="Live estimate from Realtime token usage">
          <span className="spend-label">this session</span>
          <span className="spend-value">{usd(sessionUsd)}</span>
          {costs?.today && (
            <>
              <span className="spend-label">today</span>
              <span className="spend-value">{usd(costs.today.totalUsd)}</span>
            </>
          )}
        </div>
      </header>

      {me.avatars.length === 0 && (
        <div className="empty">
          No avatars yet. Hire one from <strong>+ from marketplace</strong> above, or build your
          own with <strong>+ custom</strong>. Either way it starts with no memory, and learns only
          what you tell it.
        </div>
      )}

      <div className="controls">
        <button onClick={start} disabled={connected || me.avatars.length === 0}>
          ▶ Start conversation
        </button>
        <button onClick={stop} disabled={!connected} className="secondary">
          ■ Stop
        </button>
        <button onClick={reset} className="ghost danger">
          Reset all Delegate data
        </button>
        <button onClick={() => void refreshState()} className="ghost">
          Refresh state
        </button>
        <button onClick={() => void reconcile()} className="ghost" disabled={reconciling}>
          {reconciling ? "Reconciling…" : "Reconcile with OpenAI billing"}
        </button>
        {me.user.role === "superadmin" && (
          <button onClick={() => setShowTenants(true)} className="ghost">
            + Tenant
          </button>
        )}
      </div>

      {error && <div className="error">⚠ {error}</div>}

      <div className="grid">
        <section className="panel">
          <h2>Transcript</h2>
          <div className="transcript">
            {transcript.length === 0 && <p className="muted">Press Start and talk. Or type below.</p>}
            {transcript.map((t, i) => (
              <div key={i} className={`bubble ${t.role}`}>
                <span className="who">{t.role === "user" ? "You" : avatarName}</span>
                <span className="said">{t.text}</span>
                {t.role === "assistant" && (
                  <span className="rate">
                    <button
                      className={`rate-btn ${rated[i] === 1 ? "on up" : ""}`}
                      title="Good reply"
                      onClick={() => void rate(i, 1)}
                    >
                      👍
                    </button>
                    <button
                      className={`rate-btn ${rated[i] === -1 ? "on down" : ""}`}
                      title="Bad reply"
                      onClick={() => void rate(i, -1)}
                    >
                      👎
                    </button>
                    <button
                      className="rate-btn"
                      title="Say what was wrong"
                      onClick={() => {
                        setNoteFor(i);
                        setNoteText("");
                      }}
                    >
                      ✎
                    </button>
                  </span>
                )}
                {noteFor === i && (
                  <form className="note" onSubmit={submitNote}>
                    <input
                      autoFocus
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="What should have happened?"
                    />
                    <button type="submit">Save</button>
                  </form>
                )}
              </div>
            ))}
          </div>
          <form onSubmit={sendText} className="textbar">
            <input
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              placeholder="Type a message (same tool loop, no mic needed)"
              disabled={!connected}
            />
            <button type="submit" disabled={!connected}>
              Send
            </button>
          </form>
        </section>

        <section className="panel">
          <h2>Debug timeline</h2>
          <div className="timeline" ref={timelineRef}>
            {events.map((e, i) => (
              <div key={i} className="event">
                <span className="time">{e.timestamp.slice(11, 23)}</span>
                <span className="type" style={{ color: EVENT_COLORS[e.eventType] ?? "#475569" }}>
                  {e.eventType}
                </span>
                {e.toolName && <span className="tool">{e.toolName}</span>}
                {e.durationMs !== undefined && <span className="ms">{Math.round(e.durationMs)}ms</span>}
                {e.payloadSummary !== undefined && (
                  <span className="payload">
                    {typeof e.payloadSummary === "string"
                      ? e.payloadSummary
                      : JSON.stringify(e.payloadSummary)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </section>
      </div>

      <Briefing charter={charter} ack={briefingAck} connected={connected} resources={resources} />

      <section className="panel">
        <h2>Spend</h2>
        {costs ? (
          <div className="state">
            <div>
              <h3>this session</h3>
              <ul>
                <li>total <code>{usd(costs.session?.totalUsd ?? 0)}</code></li>
                <li>audio <code>{usd(costs.session?.audioUsd ?? 0)}</code></li>
                <li>text <code>{usd(costs.session?.textUsd ?? 0)}</code></li>
              </ul>
            </div>
            <div>
              <h3>today</h3>
              <ul>
                <li>total <code>{usd(costs.today.totalUsd)}</code></li>
                <li>audio <code>{usd(costs.today.audioUsd)}</code></li>
                {costs.today.byKind.map((k) => (
                  <li key={k.kind}>{k.kind} <code>{usd(k.costUsd)}</code></li>
                ))}
              </ul>
            </div>
            <div>
              <h3>vs actual billing</h3>
              {costs.calibration ? (
                <ul>
                  <li>factor <code>{costs.calibration.factor.toFixed(3)}</code></li>
                  <li>metered <code>{usd(costs.calibration.estimatedUsd)}</code></li>
                  <li>billed <code>{usd(costs.calibration.actualUsd)}</code></li>
                  <li>
                    corrected today <code>{usd(costs.today.calibratedUsd)}</code>
                  </li>
                  <li className="muted">
                    {costs.calibration.days}d, checked {costs.calibration.checkedAt.slice(0, 16).replace("T", " ")}
                  </li>
                </ul>
              ) : (
                <ul>
                  {billedToday !== null && (
                    <li>
                      billed today <code>{usd(billedToday)}</code>{" "}
                      <span className="muted">(OpenAI lags hours)</span>
                    </li>
                  )}
                  <li className="muted">
                    {costs.reconciliationAvailable
                      ? "No completed day to calibrate against yet — press Reconcile again tomorrow."
                      : "Set OPENAI_API_ADMIN_KEY (api.usage.read) to check these estimates against real billing."}
                  </li>
                </ul>
              )}
            </div>
          </div>
        ) : (
          <p className="muted">loading…</p>
        )}
      </section>

      <section className="panel">
        <h2>Level 3 — persistent Delegate state</h2>
        {state ? (
          <div className="state">
            <div>
              <h3>memory_facts ({state.counts.memoryFacts})</h3>
              <ul>
                {state.facts.map((f) => (
                  <li key={f.key}>
                    <code>{f.key}</code> = {JSON.stringify(f.value)}{" "}
                    <span className="muted">[{f.tags.join(", ")}]</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>tasks ({state.counts.tasks})</h3>
              <ul>
                {state.tasks.map((t) => (
                  <li key={t.title}>
                    <code>{t.title}</code> — {t.status} — {JSON.stringify(t.facts)}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3>tool_events ({state.counts.toolEvents})</h3>
              <ul>
                {state.toolEvents.map((e, i) => (
                  <li key={i}>
                    {e.success ? "✅" : "⛔"} {e.args?.reason ?? "?"} — {Math.round(e.latencyMs)}ms —{" "}
                    <span className="muted">{e.result?.instructionToRealtime?.slice(0, 80)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className="muted">loading…</p>
        )}
      </section>
    </div>
  );
}
